import * as React from 'react';

import {
  BS_CLOSE_THRESHOLD,
  BS_DURATION_MS,
  BS_TRANSITION_OPACITY,
  BS_TRANSITION_TRANSFORM,
  BS_VELOCITY_THRESHOLD,
  BS_VELOCITY_WINDOW_MS,
} from './constants';
import { prefersReducedMotion } from './utils';

/** Pure dismiss decision (extracted for testability): past 25% of height, or a fast downward flick. */
export function shouldDismiss({
  dragY,
  panelHeight,
  velocity,
}: {
  dragY: number;
  panelHeight: number;
  velocity: number;
}): boolean {
  return (
    dragY > panelHeight * BS_CLOSE_THRESHOLD || velocity > BS_VELOCITY_THRESHOLD
  );
}

interface UseDragArgs {
  enabled: boolean;
  panelRef: React.RefObject<HTMLElement | null>;
  overlayRef: React.RefObject<HTMLElement | null>;
  // Listeners attach to the handle only, so scrolling the content isn't hijacked.
  handleEl: HTMLElement | null;
  onDismiss: () => void;
}

/** Pointer drag-to-dismiss for Sheet's bottom variant, matched to vaul. */
export function useDrag({
  enabled,
  panelRef,
  overlayRef,
  handleEl,
  onDismiss,
}: UseDragArgs) {
  // Held in a ref so a parent re-render mid-gesture can't re-subscribe the
  // listeners (which would orphan the in-flight drag).
  const onDismissRef = React.useRef(onDismiss);
  React.useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  // Lets the Sheet stop a drag that's still settling (and clear its inline styles) when it
  // closes, so they don't block the close animation. Does nothing if no drag is in progress.
  const cancelReleaseRef = React.useRef<(() => void) | null>(null);
  const cancelPendingRelease = React.useCallback(() => {
    cancelReleaseRef.current?.();
  }, []);

  React.useEffect(() => {
    if (!enabled || !handleEl) return;
    const panel = panelRef.current;
    if (!panel) return;

    let startY = 0;
    let dragY = 0;
    let pointerId: number | null = null;
    let panelHeight = 1;
    let samples: Array<{ t: number; y: number }> = [];
    // Cancels a pending release animation's transitionend listener + fallback timer.
    let cancelRelease: (() => void) | null = null;
    const reduced = prefersReducedMotion();

    const setTransition = (on: boolean) => {
      panel.style.transition = on ? BS_TRANSITION_TRANSFORM : 'none';
      const overlay = overlayRef.current;
      if (overlay)
        overlay.style.transition = on ? BS_TRANSITION_OPACITY : 'none';
    };

    const clearDragStyles = () => {
      panel.removeAttribute('data-bs-dragging');
      panel.style.transform = '';
      panel.style.transition = '';
      const overlay = overlayRef.current;
      if (overlay) {
        overlay.style.opacity = '';
        overlay.style.transition = '';
      }
    };

    // Only acts while a release is still animating; a finished drag is left as-is.
    cancelReleaseRef.current = () => {
      if (cancelRelease) {
        cancelRelease();
        clearDragStyles();
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (pointerId !== null) return; // ignore a second pointer
      pointerId = e.pointerId;
      panelHeight = panel.getBoundingClientRect().height || 1;

      // If a release is still animating, grab the panel at its current rendered offset (not
      // its target) so re-grabbing is continuous instead of jumping from off-screen.
      let resumeY = 0;
      if (cancelRelease) {
        cancelRelease();
        const transform = getComputedStyle(panel).transform;
        if (transform && transform !== 'none') {
          try {
            resumeY = new DOMMatrixReadOnly(transform).m42;
          } catch {
            resumeY = 0;
          }
        }
        resumeY = Math.min(panelHeight, Math.max(0, resumeY));
      }

      startY = e.clientY - resumeY;
      dragY = resumeY;
      setTransition(false);
      if (resumeY > 0) {
        panel.style.transform = `translate3d(0, ${resumeY}px, 0)`;
        const overlay = overlayRef.current;
        if (overlay) {
          overlay.style.opacity = String(
            Math.max(0, 1 - resumeY / panelHeight)
          );
        }
      }
      samples = [{ t: e.timeStamp, y: resumeY }];
      panel.setAttribute('data-bs-dragging', 'true');
      try {
        handleEl.setPointerCapture(e.pointerId);
      } catch {
        // setPointerCapture can throw on some platforms; harmless.
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      // Downward only, clamped to the panel height so the release animation
      // never has to travel back up (which would read as a jump).
      dragY = Math.min(panelHeight, Math.max(0, e.clientY - startY));
      panel.style.transform = `translate3d(0, ${dragY}px, 0)`;
      const overlay = overlayRef.current;
      if (overlay) {
        overlay.style.opacity = String(Math.max(0, 1 - dragY / panelHeight));
      }
      samples.push({ t: e.timeStamp, y: dragY });
      while (
        samples.length > 1 &&
        samples[0].t < e.timeStamp - BS_VELOCITY_WINDOW_MS
      ) {
        samples.shift();
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      try {
        handleEl.releasePointerCapture(e.pointerId);
      } catch {
        // already released
      }
      pointerId = null;

      // Add the release point and drop samples older than the window, so speed is measured
      // at release. Otherwise a quick flick then a pause would still read as fast and dismiss.
      samples.push({ t: e.timeStamp, y: dragY });
      while (
        samples.length > 1 &&
        samples[0].t < e.timeStamp - BS_VELOCITY_WINDOW_MS
      ) {
        samples.shift();
      }

      const first = samples[0];
      const last = samples[samples.length - 1];
      const dt = Math.max(1, last.t - first.t);
      const velocity = (last.y - first.y) / dt; // px/ms, +ve = downward
      // If the OS cancels the gesture (back-swipe, palm touch, app backgrounded), snap back
      // instead of dismissing — even if it passed the threshold.
      const dismiss =
        e.type !== 'pointercancel' &&
        shouldDismiss({ dragY, panelHeight, velocity });

      panel.removeAttribute('data-bs-dragging');
      const overlay = overlayRef.current;

      // Animate the panel to `targetY` then run `done`. A transitionend drives
      // it, but a fallback timer guarantees `done` runs even when the move is a
      // no-op (released at the target) or the transition is interrupted —
      // otherwise a dismiss could hang the sheet open.
      const runRelease = (targetY: string, done: () => void) => {
        if (reduced) {
          // Reduced motion: jump straight to the end position instead of animating.
          panel.style.transform = targetY;
          done();
          return;
        }
        setTransition(true);
        panel.style.transform = targetY;
        const onEnd = (ev: TransitionEvent) => {
          if (ev.target !== panel || ev.propertyName !== 'transform') return;
          finishRelease();
        };
        const timer = setTimeout(finishRelease, BS_DURATION_MS + 50);
        function finishRelease() {
          if (!cancelRelease) return; // already run
          panel.removeEventListener('transitionend', onEnd);
          clearTimeout(timer);
          cancelRelease = null;
          done();
        }
        cancelRelease = () => {
          panel.removeEventListener('transitionend', onEnd);
          clearTimeout(timer);
          cancelRelease = null;
        };
        panel.addEventListener('transitionend', onEnd);
      };

      if (dismiss) {
        if (overlay) overlay.style.opacity = '0';
        // Continue straight down to fully closed (no jump). The inline transform
        // is left in place so the panel stays put until the consumer's close
        // lifecycle unmounts it (or a reopen clears it).
        runRelease('translate3d(0, 100%, 0)', () => onDismissRef.current());
      } else {
        if (overlay) overlay.style.opacity = '1';
        runRelease('translate3d(0, 0, 0)', () => clearDragStyles());
      }
    };

    handleEl.addEventListener('pointerdown', onPointerDown);
    handleEl.addEventListener('pointermove', onPointerMove);
    handleEl.addEventListener('pointerup', onPointerUp);
    handleEl.addEventListener('pointercancel', onPointerUp);

    return () => {
      cancelReleaseRef.current = null;
      handleEl.removeEventListener('pointerdown', onPointerDown);
      handleEl.removeEventListener('pointermove', onPointerMove);
      handleEl.removeEventListener('pointerup', onPointerUp);
      handleEl.removeEventListener('pointercancel', onPointerUp);
      // Cancel any pending release animation (transitionend listener + fallback).
      if (cancelRelease) cancelRelease();
      // If torn down mid-gesture, undo the DOM side-effects onPointerDown set so
      // the panel can't be left wedged with a stale transform / dragging attr.
      if (pointerId !== null) {
        try {
          handleEl.releasePointerCapture(pointerId);
        } catch {
          // already released
        }
        clearDragStyles();
      }
    };
  }, [enabled, panelRef, overlayRef, handleEl]);

  return { cancelPendingRelease };
}
